import type { SupabaseClient } from '@supabase/supabase-js'
import {
  type AccountFeeConfig,
  type AccountFeeRule,
  type AccountFeeRuleForm,
  feeRuleFormToRow,
  isFeeScopeKey,
} from './accountFeeRules'

export async function fetchAccountFeeConfig(
  client: SupabaseClient,
  accountId: string
): Promise<AccountFeeConfig> {
  const [accountResult, rulesResult] = await Promise.all([
    client.from('accounts').select('fee_rules_unified').eq('id', accountId).maybeSingle(),
    client.from('account_fee_rules').select('*').eq('account_id', accountId),
  ])

  if (accountResult.error) throw accountResult.error
  if (rulesResult.error) throw rulesResult.error

  const rules = ((rulesResult.data || []) as AccountFeeRule[]).filter((row) => isFeeScopeKey(row.market))

  return {
    fee_rules_unified: false,
    rules,
  }
}

export async function fetchFeeConfigsForAccounts(
  client: SupabaseClient,
  accountIds: string[]
): Promise<Map<string, AccountFeeConfig>> {
  const map = new Map<string, AccountFeeConfig>()
  if (!accountIds.length) return map

  const rulesResult = await client.from('account_fee_rules').select('*').in('account_id', accountIds)
  if (rulesResult.error) throw rulesResult.error

  const rulesByAccount = new Map<string, AccountFeeRule[]>()
  for (const row of (rulesResult.data || []) as AccountFeeRule[]) {
    if (!isFeeScopeKey(row.market)) continue
    const list = rulesByAccount.get(row.account_id!) || []
    list.push(row)
    rulesByAccount.set(row.account_id!, list)
  }

  for (const accountId of accountIds) {
    map.set(accountId, {
      fee_rules_unified: false,
      rules: rulesByAccount.get(accountId) || [],
    })
  }

  return map
}

export async function saveAccountFeeRules(
  client: SupabaseClient,
  accountId: string,
  enabledScopes: string[],
  forms: Record<string, AccountFeeRuleForm>
): Promise<void> {
  const { error: accountError } = await client
    .from('accounts')
    .update({ fee_rules_unified: false, updated_at: new Date().toISOString() })
    .eq('id', accountId)
  if (accountError) throw accountError

  const { error: deleteError } = await client.from('account_fee_rules').delete().eq('account_id', accountId)
  if (deleteError) throw deleteError

  const rows: AccountFeeRule[] = []
  for (const scopeKey of enabledScopes) {
    const row = feeRuleFormToRow(scopeKey, forms[scopeKey] || {})
    if (row) rows.push(row)
  }

  if (!rows.length) return

  const { error: insertError } = await client.from('account_fee_rules').insert(
    rows.map((row) => ({
      account_id: accountId,
      market: row.market,
      fixed_fee: row.fixed_fee,
      min_fee: row.min_fee,
      threshold_amount: row.threshold_amount,
      fee_below_threshold: row.fee_below_threshold,
      percent_above_threshold: row.percent_above_threshold,
    }))
  )
  if (insertError) throw insertError
}
